import { useContext, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import MyTitle from "../../components/MyTitle";
import { useBoardItemComment } from "../../hooks/useBoardItemComment";
import MyLoading from "../../components/MyLoading";
import { AuthContext } from "../../context/AuthProvider";
import { PopupContext } from "../../context/PopupProvider";
import DeleteBoardItemCommentDialog from "../Common/DeleteBoardItemCommentDialog";
import EnterWriterPasswordDialog from "../Common/EnterWriterPasswordDialog";
import { SiteContext } from "../../context/SiteProvider";
import CommentForm from "./CommentForm";

const CommentList = ({ board, boardItem }) => {
  const {
    useGetBoardItemComments,
    deleteBoardItemCommentMutation,
    verifyPasswordViewCommentMutation,
    verifyPasswordEditCommentMutation,
  } = useBoardItemComment();
  const { isAuthenticated, profile, isAdmin } = useContext(AuthContext);
  const { openPopup } = useContext(PopupContext);
  const { setLoadingScreen } = useContext(SiteContext);
  const queryClient = useQueryClient();

  const privateViewToken = new URLSearchParams(window.location.search).get(
    "privateViewToken"
  );

  const [selectComment, setSelectComment] = useState(null);

  const { data, isLoading } = useGetBoardItemComments(
    {
      size: 10000,
      page: 1,
      sort: "createdDate,Asc",
      boardCode: board?.boardCode,
      itemNo: boardItem?.itemNo,
      rawPrivateViewToken: privateViewToken,
      useComment: board?.options?.useComment,
    },
    (data) => data.data
  );

  const openPopupDelete = (commentNo) =>
    openPopup(
      <DeleteBoardItemCommentDialog
        boardCode={board?.boardCode}
        itemNo={boardItem?.itemNo}
        commentNo={commentNo}
      />
    );

  const openPopupSecretEditComment = (comment) => {
    openPopup(
      <EnterWriterPasswordDialog
        type="comment"
        params={{
          boardCode: board?.boardCode,
          itemNo: boardItem?.itemNo,
          commentNo: comment.commentNo,
        }}
        permission="edit"
        onAction={(res) =>
          verifyPasswordEditCommentMutation.mutate(
            {
              boardCode: board?.boardCode,
              itemNo: boardItem?.itemNo,
              commentNo: comment.commentNo,
              privateViewToken: res?.privateViewToken,
              rawPrivateViewToken: privateViewToken,
              payload: res?.payload,
            },
            {
              onSuccess: (res2) => {
                if (res2?.anonymousEditToken) {
                  setSelectComment({
                    ...comment,
                    privateViewToken: res.privateViewToken,
                    anonymousEditToken: res2.anonymousEditToken,
                  });
                }
              },
            }
          )
        }
        mutation={verifyPasswordViewCommentMutation}
      />
    );
  };

  const openPopupSecretDeleteComment = (comment) => {
    openPopup(
      <EnterWriterPasswordDialog
        type="comment"
        params={{
          boardCode: board?.boardCode,
          itemNo: boardItem?.itemNo,
          commentNo: comment.commentNo,
        }}
        permission="delete"
        onAction={(res) =>
          verifyPasswordEditCommentMutation.mutate(
            {
              boardCode: board?.boardCode,
              itemNo: boardItem?.itemNo,
              commentNo: comment.commentNo,
              privateViewToken: res?.privateViewToken,
              rawPrivateViewToken: privateViewToken,
              payload: res?.payload,
            },
            {
              onSuccess: (res2) => {
                if (res2?.anonymousEditToken) {
                  deleteBoardItemCommentMutation.mutate(
                    {
                      boardCode: board?.boardCode,
                      itemNo: boardItem?.itemNo,
                      commentNo: comment.commentNo,
                      privateViewToken: res?.privateViewToken,
                      rawPrivateViewToken: privateViewToken,
                    },
                    {
                      onSuccess: () => {
                        alert("댓글이 삭제되었습니다.");
                        queryClient.invalidateQueries({
                          queryKey: [
                            "/board-item-comment?boardCode={boardCode}&itemNo={itemNo}&page=1",
                          ],
                        });
                      },
                      onError: () => {
                        alert("삭제 취소됨");
                      },
                      onSettled: () => {
                        setLoadingScreen(false);
                      },
                    }
                  );
                }
              },
            }
          )
        }
        mutation={verifyPasswordViewCommentMutation}
      />
    );
  };

  return isLoading ? (
    <MyLoading className="h-[150px]" />
  ) : (
    <div className="overflow-x-auto max-w-full">
      {data?.content?.length > 0 && (
        <div>
          <MyTitle
            title={`댓글 ${
              data?.totalElements ? `(${data.totalElements})` : ""
            }`}
            className="mb-4"
          />
          <table className="w-full border-collapse border-t-2 border-primary">
            <tbody>
              {data.content.map((comment) =>
                selectComment &&
                selectComment.commentNo === comment.commentNo ? (
                  <tr key={comment.commentNo}>
                    <td colSpan={3} className="border border-gray-border p-4">
                      <CommentForm
                        board={board}
                        boardItem={boardItem}
                        selectComment={selectComment}
                        setSelectComment={setSelectComment}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={comment.commentNo}>
                    <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                      {comment?.writer?.name}
                      <br />
                      <span className="2xl:text-sm text-xs">
                        {dayjs(comment?.createdDate).format("YYYY-MM-DD")}
                      </span>
                    </th>
                    <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto whitespace-pre-wrap">
                      {comment?.comment || "-"}
                    </td>
                    <td className="border border-gray-border text-gray-dark md:w-[15%] w-[80px]">
                      <div className="flex items-center justify-center gap-2">
                        {(isAuthenticated &&
                          profile.userId === comment?.writer?.userId) ||
                        isAdmin ? (
                          // action for member
                          <>
                            <button
                              className="text-gray-dark font-medium 2xl:text-base text-sm"
                              onClick={() => openPopupDelete(comment.commentNo)}
                            >
                              삭제
                            </button>
                            <div className="h-2 w-[1px] bg-gray-dark" />
                            <button
                              className="text-gray-dark font-medium 2xl:text-base text-sm"
                              onClick={() => setSelectComment(comment)}
                            >
                              수정
                            </button>
                          </>
                        ) : !isAuthenticated && !comment.writer?.userId ? (
                          // action for non-member
                          <>
                            <button
                              className="text-gray-dark font-medium 2xl:text-base text-sm"
                              onClick={() =>
                                openPopupSecretDeleteComment(comment)
                              }
                            >
                              삭제
                            </button>
                            <div className="h-2 w-[1px] bg-gray-dark" />
                            <button
                              className="text-gray-dark font-medium 2xl:text-base text-sm"
                              onClick={() =>
                                openPopupSecretEditComment(comment)
                              }
                            >
                              수정
                            </button>
                          </>
                        ) : (
                          <></>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CommentList;
